import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CommentsSheet.css";

/**
 * Long-press → action sheet, for comment Edit / Delete on tablet and phone.
 *
 * On a wide screen a comment carries a "⋮" button: it costs nothing there, and
 * a cursor can hit a 28px target precisely. On a handset that same button had
 * to be squeezed into a column at every nesting level, and it is the only
 * affordance in the thread that is not the content itself — so it reads as
 * clutter on exactly the screen with the least room for it.
 *
 * Press-and-hold is what the platform already teaches for "act on this item"
 * (iOS, Android, WhatsApp, Instagram), so the button goes away below 1024px and
 * the gesture takes over. Desktop keeps the "⋮" untouched.
 */

/**
 * One hook call per component; returns a BINDER you call per comment:
 *
 *     const longPress = useLongPressBinder();
 *     <div {...longPress(() => openActions(c), { enabled })} />
 *
 * It is deliberately not `useLongPress(...)` called per row — that would be a
 * hook inside a map, which React forbids and which breaks the moment the list
 * length changes. The timer refs are shared because only one finger can be down
 * at a time, so a single in-flight press is the whole truth.
 *
 * A drag must NOT fire it: the thread scrolls under the finger, and a list you
 * cannot scroll without triggering menus is worse than no gesture at all. Any
 * movement past `slop` cancels the timer.
 */
export function useLongPressBinder({ delay = 450, slop = 8 } = {}) {
  const timer = useRef(null);
  const moved = useRef(false);
  const origin = useRef({ x: 0, y: 0 });

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // A press in flight when the thread unmounts would fire into a dead component.
  useEffect(() => clear, [clear]);

  return useCallback(
    (onLongPress, { enabled = true } = {}) => {
      if (!enabled) return {};
      return {
        onTouchStart: (e) => {
          // A reply's row is rendered INSIDE its parent comment's row, so one
          // touch fires this on the reply and then again on the parent as it
          // bubbles. With shared timer refs the parent — firing second —
          // overwrote the reply's press, so long-pressing a reply opened the
          // sheet for the PARENT, and confirming Delete there cascaded the
          // whole thread. The innermost row claims the press; ancestors then
          // see it is spoken for and keep out of it.
          //
          // Marked on the NATIVE event, which is the one object shared by every
          // handler on the bubble path — a React synthetic event is not.
          // Deliberately not stopPropagation(): other handlers on the way up
          // (scroll, focus, the sheet's own drag) still have every right to
          // this touch.
          if (e.nativeEvent.__longPressClaimed) return;
          e.nativeEvent.__longPressClaimed = true;

          const t = e.touches[0];
          moved.current = false;
          origin.current = { x: t.clientX, y: t.clientY };
          clear();
          timer.current = setTimeout(() => {
            timer.current = null;
            if (!moved.current) onLongPress();
          }, delay);
        },
        onTouchMove: (e) => {
          const t = e.touches[0];
          if (
            Math.abs(t.clientX - origin.current.x) > slop ||
            Math.abs(t.clientY - origin.current.y) > slop
          ) {
            moved.current = true;
            clear();
          }
        },
        onTouchEnd: clear,
        onTouchCancel: clear,
        // Android fires the native text/context menu on the same gesture;
        // without this you get ours and the browser's at once.
        onContextMenu: (e) => e.preventDefault(),
      };
    },
    [clear, delay, slop],
  );
}

/**
 * The sheet the gesture opens. `items` is [{ label, icon, onClick, danger }].
 * Shares CommentsSheet.css so both sheets slide in identically.
 */
export function CommentActionSheet({ open, items = [], onClose, title, busy = false }) {
  // A destructive item confirms IN PLACE — a second stacked modal on a phone
  // means two scrims, two slide-ins and a jump away from the thing you were
  // pointing at, for one yes/no. The sheet is already the surface in front of
  // you, so the question belongs in it.
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && (pending ? setPending(null) : onClose?.());
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, pending]);

  // Reopening on another comment must not inherit the last one's confirm step.
  useEffect(() => {
    if (!open) setPending(null);
  }, [open]);

  if (!open) return null;

  const dismiss = () => {
    if (busy) return; // never yank the sheet out from under an in-flight delete
    setPending(null);
    onClose?.();
  };

  return createPortal(
    <>
      {/* z-index above CommentsSheet (9999): this opens FROM the comments sheet
          on a phone, so it has to sit over it rather than behind it. */}
      <div
        className="comments-sheet__scrim fixed inset-0 z-[10000] bg-ink/45"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || "Comment actions"}
        className="comments-sheet fixed inset-x-0 bottom-0 z-[10001] rounded-t-[22px] bg-surface"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}
      >
        <div className="grid place-items-center pb-1.5 pt-2.5">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        {pending ? (
          /* ── confirm step, in place of the action list ── */
          <>
            <div className="px-6 pb-1 pt-2 text-center">
              <p className="text-[16px] font-bold text-gray-900">{pending.confirm.title}</p>
              {pending.confirm.message ? (
                <p className="mt-1.5 text-[13px] leading-relaxed text-gray-500">
                  {pending.confirm.message}
                </p>
              ) : null}
            </div>
            <div className="px-3.5 pb-1 pt-4">
              <button
                type="button"
                disabled={busy}
                onClick={() => pending.onClick?.()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-[15px] font-bold text-white transition active:bg-red-700 disabled:opacity-60"
              >
                {busy ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : null}
                {pending.confirm.confirmLabel || "Delete"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setPending(null)}
                className="mt-1.5 w-full rounded-xl px-4 py-3 text-center text-[15px] font-semibold text-gray-500 transition active:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {title ? (
              <p className="truncate border-b border-gray-100 px-5 pb-3 text-center text-[12.5px] text-gray-400">
                {title}
              </p>
            ) : null}

            <div className="px-2 py-1.5">
              {items.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  onClick={() => {
                    // An item that asks a question stays in the sheet; the rest
                    // close it and run, as before.
                    if (it.confirm) {
                      setPending(it);
                      return;
                    }
                    onClose?.();
                    it.onClick?.();
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left text-[15px] font-semibold transition active:bg-gray-100 ${
                    it.danger ? "text-red-600 dark:text-red-300" : "text-gray-800"
                  }`}
                >
                  <span className="grid h-5 w-5 shrink-0 place-items-center">{it.icon}</span>
                  {it.label}
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 px-2 pt-1.5">
              <button
                type="button"
                onClick={dismiss}
                className="w-full rounded-xl px-3.5 py-3 text-center text-[15px] font-semibold text-gray-500 transition active:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}

/* Shared glyphs so both feeds show the same action icons. */
export const EditIcon = (
  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4v16h16v-7M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

export const DeleteIcon = (
  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3" />
  </svg>
);
